/**
 * Shared helpers for inline reference tokens inside free-form comment bodies.
 *
 * Two token grammars share one shape. A user mention is stored inline as:
 *
 *   @[Display Name](user:<uuid>)
 *
 * and a workflow-node reference — used by node-anchored comments such as the
 * deactivation-reason comment — as:
 *
 *   @[Step label](node:<uuid>)
 *
 * so the canonical body string round-trips through the DB, the rich-text
 * editor, and markdown-style renderers without additional metadata.
 *
 * The two grammars are deliberately kept as separate regexes rather than one
 * alternation: `extractMentionUserIds` feeds notification fanout, and a node
 * reference must never be mistaken for a person to notify.
 */

const UUID = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';

/** Regex matching a mention token. Recreate per call to avoid shared lastIndex. */
export const mentionRegex = (): RegExp =>
  new RegExp(`@\\[([^\\]]+)\\]\\(user:(${UUID})\\)`, 'g');

export interface ParsedMention {
  displayName: string;
  userId: string;
  raw: string;
  start: number;
  end: number;
}

/** Extract all mention tokens from a body string, preserving source order. */
export function parseMentions(body: string): ParsedMention[] {
  const regex = mentionRegex();
  const out: ParsedMention[] = [];
  let match: RegExpExecArray | null;
  while ((match = regex.exec(body)) !== null) {
    out.push({
      displayName: match[1]!,
      userId: match[2]!.toLowerCase(),
      raw: match[0],
      start: match.index,
      end: match.index + match[0].length,
    });
  }
  return out;
}

/** Unique mentioned user IDs (lowercased) in source order. */
export function extractMentionUserIds(body: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const m of parseMentions(body)) {
    if (seen.has(m.userId)) continue;
    seen.add(m.userId);
    out.push(m.userId);
  }
  return out;
}

/** Canonical rendering of a mention token from its parts. */
export function formatMention(displayName: string, userId: string): string {
  return `@[${displayName}](user:${userId})`;
}

/** Regex matching a workflow-node reference token. Recreate per call to avoid shared lastIndex. */
export const nodeRefRegex = (): RegExp =>
  new RegExp(`@\\[([^\\]]+)\\]\\(node:(${UUID})\\)`, 'g');

export interface ParsedNodeRef {
  label: string;
  nodeId: string;
  raw: string;
  start: number;
  end: number;
}

/** Extract all node-reference tokens from a body string, preserving source order. */
export function parseNodeRefs(body: string): ParsedNodeRef[] {
  const regex = nodeRefRegex();
  const out: ParsedNodeRef[] = [];
  let match: RegExpExecArray | null;
  while ((match = regex.exec(body)) !== null) {
    out.push({
      label: match[1]!,
      nodeId: match[2]!.toLowerCase(),
      raw: match[0],
      start: match.index,
      end: match.index + match[0].length,
    });
  }
  return out;
}

/**
 * Canonical rendering of a node-reference token from its parts.
 *
 * The label is sanitized because it comes from user-controlled node titles: a
 * `]` or a newline inside it would truncate the token and leave the raw tail
 * visible as literal text. Collapses whitespace and drops bracket characters.
 */
export function formatNodeRef(label: string, nodeId: string): string {
  const safeLabel = label.replace(/[[\]]/g, '').replace(/\s+/g, ' ').trim() || 'step';
  return `@[${safeLabel}](node:${nodeId})`;
}
